/**
 * Paths that must NEVER be handed to the mobile app (CU-86akjut0z).
 *
 * The app claims every path on the portal domain as a Universal Link / App Link.
 * That is correct for patient content — portal routes map 1:1 onto the mobile
 * app's Expo Router tree, so those links open the right screen. It is WRONG for
 * routes whose behaviour depends on browser state the app does not have:
 * single-use credentials in the URL, a Stripe PaymentIntent created in the
 * browser session, or a WebAuthn ceremony bound to the canonical RP host.
 *
 * When one of those is intercepted, the app shows a 404 and the credential is
 * consumed. The patient cannot retry — a magic link is single-use.
 *
 * ⚠️ THIS LIST IS CONSUMED BY THREE SURFACES AND THEY MUST NOT DRIFT:
 *   1. The AASA `exclude` rules (iOS)     — src/prodSiteAssociation.json + staging
 *   2. Android `pathAdvancedPattern`      — mideo-health-app/app.config.js
 *   3. The iOS Smart App Banner           — src/routes/__root.tsx
 *
 * Surfaces 1 and 2 are static config in files this module cannot reach (one of
 * them in another repo), so they are verified against this list by test rather
 * than generated from it. See browserOnlyPaths.test.ts — if you add an entry
 * here, that test tells you exactly which config files to update.
 *
 * ⚠️ Excluding a path that did not need it is CHEAP (the link opens in the
 * browser, where it works). Claiming a path that needed excluding SPENDS A
 * CREDENTIAL and is unrecoverable for that patient. When unsure, exclude.
 *
 * ⚠️ BUT "cheap" is not "free", and the asymmetry is not a licence to exclude
 * anything plausible. `/login` was excluded here on the reasoning that it runs
 * a WebAuthn ceremony bound to rpId app.mideohealth.com — TRUE of the portal's
 * browser implementation (@simplewebauthn/browser), and IRRELEVANT to the app,
 * which has a working native login screen (mideo-health-app
 * src/app/login.tsx → LoginManagerScreen, native passkeys via usePasskeyLogin;
 * it does not import @simplewebauthn at all). Excluding it protected nothing
 * and broke a working deep link. Removed 2026-09-17.
 *
 * The lesson generalises: before adding a path here, check whether the MOBILE
 * APP has a screen for it. A path with a working app screen belongs in the
 * app unless the URL itself carries browser-bound state.
 */

/**
 * Matched against `window.location.pathname`. Each entry is a path prefix; a
 * path matches if it equals the prefix or continues with `/` or `?`.
 *
 * `*` is a single-segment wildcard, used for the patient-id first segment
 * (`/{patientId}/payment/thanks`). It never matches across a `/`.
 */
export const BROWSER_ONLY_PATHS = [
  /**
   * Firebase email-link (magic link) sign-in. Reads a single-use `oobCode`,
   * plus `apiKey`, `mode`, `email`, `destination`, `language`. Interception
   * spends the code without signing anyone in. THE reported bug.
   */
  '/concierge',

  /**
   * Stripe `return_url` (see components/payment/CheckoutForm.tsx). Stripe
   * appends `payment_intent_client_secret`, which is read back via
   * `stripe.retrievePaymentIntent()`. That call must run in the context that
   * created the PaymentIntent — the browser — or the patient loses the
   * confirmation after a 3DS/bank redirect.
   */
  '/*/payment/thanks',

  /**
   * Initiates the Stripe confirm whose return_url is `/payment/thanks` above.
   * Kept in the browser so both ends of the redirect share one context.
   */
  '/*/payment/selfPay',

  /**
   * ⚠️ Firebase's DEFAULT action handler — the highest-value entry in this list.
   *
   * It carries a single-use `oobCode` for EVERY Firebase email action the
   * product issues: password reset (verifyPasswordResetCode /
   * confirmPasswordReset), email verification (applyActionCode) and
   * recover-email. Plus `apiKey` and `mode`.
   *
   * ⚠️ IT IS NOT A PORTAL ROUTE. It is a Firebase Hosting reserved namespace
   * (priority 1, above redirects and static content), so it appears NOWHERE in
   * routeTree.gen.ts — and the host that serves it is configured in the
   * FIREBASE CONSOLE (Auth → Templates → customize action URL), not in any
   * repo. No route-tree enumeration, grep or PR diff can surface it. The only
   * way to find it is to ask which URLs get EMAILED to patients.
   *
   * Confirmed live in production: Sentry MIDEO-UNIFIED-7X, 3 patients,
   * 2026-07-24, App Store build — "Unmatched route (NotFound): /__/auth/action"
   * with the raw oobCode in searchParams, isNavReady true (so not a cold-start
   * race). Three events in three minutes: one patient re-requesting the email
   * and burning a fresh code each time.
   *
   * Since the MFA hard cutover (enforcement live 2026-09-01) password reset is
   * a load-bearing recovery path, which makes this worse than /concierge.
   *
   * ⚠️ The Smart App Banner CANNOT mitigate this one: the React app never
   * mounts on this path, so __root.tsx never renders. The AASA rule and the
   * Android pattern are the only levers.
   */
  '/__/auth',

  /**
   * Excluded defensively. Reads `localStorage.initialEmail` rather than URL
   * params today, so it may not strictly need this — but whether an email flow
   * can land here with credentials in the URL is unconfirmed, and the cost of
   * being wrong is asymmetric (see the header note).
   */
  '/enroll',

  /**
   * Static legal text, and the one page a non-patient might open. Product
   * decision 2026-09-17: keep it in the browser.
   */
  '/privacy',

  /** Dev/test route. Excluded in the AASA since CU-86aj1db1q. */
  '/testErrors',
] as const;

/**
 * True if `pathname` is a browser-only route that must not open in the app.
 *
 * Case-insensitive: these paths are camelCase (`payment/selfPay`,
 * `testErrors`), and a patient following a link with different casing should
 * still be kept out of the app. The AASA rules set `"caseSensitive": false` to
 * match this behaviour.
 */
export function isBrowserOnlyPath(pathname: string): boolean {
  // Ignore any query string / hash, and normalise a trailing slash so
  // "/concierge/" and "/concierge" behave identically.
  const path = pathname.split(/[?#]/)[0].replace(/\/+$/, '') || '/';

  return BROWSER_ONLY_PATHS.some((pattern) => {
    const patternSegments = pattern.split('/');
    const pathSegments = path.split('/');

    // The path must be at least as deep as the pattern. A LONGER path is a
    // match (a sub-path of an excluded route is also excluded), which is what
    // the trailing `*` in the AASA rules expresses.
    if (pathSegments.length < patternSegments.length) return false;

    return patternSegments.every(
      (segment, i) => segment === '*' || segment.toLowerCase() === pathSegments[i].toLowerCase(),
    );
  });
}
